import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import {
  ActivityLogDTO,
  DncDetailsDTO,
  ILead,
  LogAction,
  LogContextType,
  assignDncDetails,
  saveActivityLog,
  updateLeadStatus,
} from 'wisestep-api';
import { getFormatDate, WsDrawer, WsPageHeader } from 'wisestep-base';

import openNotesFormDialog from '../../../../features/clientDetails/helper/openNotesFormDialog';
import DataPrivacy from '../../../../features/conversationWidget/components/DataPrivacy';
import { useDialog } from '../../../../hooks/useDialog';
import { LeadAccess, useLeadAuthz } from '../../../../hooks/useLeadAuthz';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { openUrlInNewTab } from '../../../../utils/openUrlInNewTab';
import {
  LeadUpdateDialog,
  defaultLeadActivityLogDTO,
  limitedPrivacyOptions,
  getLeadStatusByName,
} from '../../helper/leadHelper';
import CreateLeadDrawer from '../CreateLeadDrawer';

import LeadOwnerUpdateDialog from './LeadOwnerUpdateDialog';
import LeadStatusUpdateDialog from './LeadStatusUpdateDialog';
interface ILeadDetailsHeaderProp {
  lead: ILead;
  setLead: Dispatch<SetStateAction<ILead>>;
  queryData: string;
}

const LeadDetailsHeader = ({ lead, setLead }: ILeadDetailsHeaderProp) => {
  const [leadUpdateDialog, setLeadUpdateDialog] = useState<LeadUpdateDialog | null>(null);
  const headerEditButtonRef = useRef<HTMLElement | null>(null);
  const isAuthroizedEditLeadDetails = useLeadAuthz(LeadAccess.EDIT_LEAD_DETAILS);
  const [openDialog, closeDialog] = useDialog();
  const [openDNC, setOpenDNC] = useState<boolean>(false);
  const [selectedLead, setSelectedLead] = useState<ILead[]>([]);
  const [createLeadDrawerOpen, setCreateLeadDrawerOpen] = useState<boolean>(false);
  const [moreOptionList, setMoreoptionList] = useState<string[]>([]);

  const onSave = async (updatedLead: ILead) => {
    try {
      if (leadUpdateDialog === LeadUpdateDialog.STATUS) {
        const oldStatus = lead.status;
        const newStatus = updatedLead.status;
        const logPayload: ActivityLogDTO = {
          ...defaultLeadActivityLogDTO,
          contextData: {},
          action: LogAction.Edit,
          field: 'Lead Status',
          description: `updated Lead Status from ${oldStatus} to${newStatus} for ${updatedLead.leadName}`,
          entityId: updatedLead.id,
          entityName: updatedLead.leadName,
          parentEntitiyType: LogContextType.Lead,
          parentEntitiyId: updatedLead.id,
        };
        await saveActivityLog(logPayload);
      } else if (leadUpdateDialog === LeadUpdateDialog.OWNER) {
        const oldOwner = lead.leadOwnerInfo?.name;
        const newOwner = updatedLead.leadOwnerInfo?.name;
        const logPayload: ActivityLogDTO = {
          ...defaultLeadActivityLogDTO,
          contextData: {},
          action: LogAction.Edit,
          field: 'Lead Owner',
          description: `updated Lead Owner from ${oldOwner} to ${newOwner} for ${updatedLead.leadName}`,
          entityId: updatedLead.id,
          entityName: updatedLead.leadName,
          parentEntitiyType: LogContextType.Lead,
          parentEntitiyId: updatedLead.id,
        };
        await saveActivityLog(logPayload);
      }

      closeLeadUpdateDialog();
      headerEditButtonRef.current?.click();
      setLead(updatedLead);
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const closeLeadUpdateDialog = () => setLeadUpdateDialog(null);
  const handleLeadEditClick = () => setCreateLeadDrawerOpen(true);

  const handleDncClick = () => {
    setOpenDNC(!openDNC);
    setSelectedLead(lead);
  };
  const navigateToJobsListing = () => {
    const encodedQueryParams = getEncodedQueryParams({
      clientNames: [lead.clientContact?.clientName],
      clientList: [{ name: lead.clientContact?.clientName }],
      clientRecruiterEmails: [lead.clientContact?.emailAddress],
      clientRecruiterList: [
        {
          clientName: lead.clientContact?.clientName,
          emailAddress: lead.clientContact?.emailAddress,
        },
      ],
      jobStatus: 'All',
      query: '',
      page: 0,
      size: 20,
      sort: [],
      workspaceId: '',
    });
    openUrlInNewTab(`/jobs?d=${encodedQueryParams}`);
  };
  const isInDncValue = lead.clientContact?.dncDetails?.isInDnc;

  useEffect(() => {
    const list = ['Add Note'];
    if (isAuthroizedEditLeadDetails) {
      list.push('Update Status');
      list.push('Change Lead Owner');
      list.push('Edit Lead');
    }
    list.push('Jobs');
    !isInDncValue && list.push('Mark DNC');
    setMoreoptionList(list);
  }, [isAuthroizedEditLeadDetails, isInDncValue]);

  const handleDncSubmit = async (leadId: string, contactId: string, data: DncDetailsDTO) => {
    try {
      const leadStatus = await getLeadStatusByName('Closed');
      await assignDncDetails(contactId, data);
      if (data.isInDnc) {
        await updateLeadStatus({
          id: leadId,
          leadName: '',
          source: '',
          leadStatus: { id: leadStatus.id },
        } as ILead);
      }
    } catch (error) {
      console.error('Error updating DNC details / lead status:', error);
    }
  };

  const handleMoreOptions = (option: string) => {
    option === 'Add Note' &&
      openNotesFormDialog({
        clientId: lead?.client?.id ?? '',
        contactId: lead.clientContact?.id ?? '',
        clientName: lead.client?.name,
        contactName: lead.clientContact?.firstName,
        isClient: false,
        openDialog: openDialog,
        closeDialog: closeDialog,
      });
    option === 'Update Status' && setLeadUpdateDialog(LeadUpdateDialog.STATUS);
    option === 'Change Lead Owner' && setLeadUpdateDialog(LeadUpdateDialog.OWNER);
    option === 'Edit Lead' && handleLeadEditClick();
    option === 'Jobs' && navigateToJobsListing();
    option === 'Mark DNC' && handleDncClick();
  };
  const addSpaces = (count: number): string => '\u00A0'.repeat(count);

  return (
    <>
      <WsPageHeader
        title={lead.leadName || ''}
        subtitle={`Created on ${getFormatDate(lead.createdOn ?? '')}${addSpaces(
          8
        )}Last Activity: ${getFormatDate(lead.modifiedOn ?? '')}`}
        moreOptions={moreOptionList}
        onMoreOptionClick={(index) => handleMoreOptions(index)}
        badgeLable={lead?.leadStatus?.leadStatusName}
        socialUrl={lead.linkedinUrl}
      />
      {leadUpdateDialog == LeadUpdateDialog.STATUS && (
        <LeadStatusUpdateDialog onSave={onSave} onCancel={closeLeadUpdateDialog} lead={lead} />
      )}
      {leadUpdateDialog == LeadUpdateDialog.OWNER && (
        <LeadOwnerUpdateDialog onSave={onSave} onCancel={closeLeadUpdateDialog} lead={lead} />
      )}
      {createLeadDrawerOpen && (
        <CreateLeadDrawer
          open={createLeadDrawerOpen}
          onClose={() => setCreateLeadDrawerOpen(false)}
          leadId={lead.id}
          postSubmit={(lead) => {
            setLead(lead);
            setCreateLeadDrawerOpen(false);
          }}
        />
      )}
      {openDNC && selectedLead && (
        <WsDrawer open={openDNC} onClose={() => setOpenDNC(!openDNC)}>
          <DataPrivacy
            clientContactParentId={selectedLead?.clientContact?.clientId}
            clientContactId={selectedLead.clientContact?.id}
            clientContactName={selectedLead.clientContact?.firstName}
            email={selectedLead?.clientContact?.createdBy ?? ''}
            candidateName={selectedLead?.leadName ?? ''}
            isInDnc={selectedLead?.clientContact?.dncDetails?.isDnc ?? false}
            dncDetails={selectedLead?.clientContact?.dncDetails}
            privacyOptions={limitedPrivacyOptions}
            restrictLabel="DNC Up to*"
            entityId={selectedLead?.id}
            parentEntitiyId={selectedLead?.id ?? ''}
            entityName={selectedLead?.leadName}
            logContextType={LogContextType.Lead}
            onSubmitApi={(data) =>
              handleDncSubmit(selectedLead.id, selectedLead.clientContact?.id, data)
            }
            enableActivityLog={true}
          />
        </WsDrawer>
      )}
    </>
  );
};

export default LeadDetailsHeader;
